import { ChangeDetectionStrategy, Component, inject, input, output, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { ZagDatePipe, ZagIcon } from '@zag/ui';

/**
 * Data shape for the API key list row / card components.
 * Mirrors ApiKeyListItem but only the fields needed for display.
 */
export interface ApiKeyListRowData {
  id: string;
  name: string | null;
  start: string | null;
  enabled: boolean;
  expiresAt: Date | null;
  createdAt: Date;
  inheritsAllPermissions: boolean;
  restrictedPermissionCount: number;
}

/**
 * Displays a single API key in a bordered list-row card.
 *
 * Follows the same pattern as `RoleListRow` in `libs/common/ui`:
 * - Individual bordered card (not separator-based)
 * - role="button" + tabindex + keyboard activation
 * - Row click navigates to edit
 * - Explicit edit + delete action buttons
 * - Scope badge (inherit vs restricted)
 */
@Component({
  selector: 'sc-api-key-list-row',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [TranslatePipe, ZagIcon, ZagDatePipe],
  host: {
    '[class.sc-api-key-list-row]': 'true',
    '[attr.role]': '"button"',
    '[attr.tabindex]': '0',
    '(click)': 'onRowClick()',
    '(keydown)': 'onRowKeydown($event)',
  },
  template: `
    <span zagIcon="vpn_key" class="sc-api-key-list-row__icon"></span>

    <div class="sc-api-key-list-row__info">
      <span class="sc-api-key-list-row__name">{{ apiKey().name || ('apiKeys.unnamed' | translate) }}</span>
      @if (apiKey().start) {
        <span class="sc-api-key-list-row__prefix">{{ apiKey().start }}…</span>
      }
    </div>

    <div class="sc-api-key-list-row__stats">
      <span
        class="sc-api-key-list-row__stat"
        [class.sc-api-key-list-row__stat--success]="apiKey().inheritsAllPermissions"
        [class.sc-api-key-list-row__stat--warning]="!apiKey().inheritsAllPermissions"
      >
        <span zagIcon="key"></span>
        @if (apiKey().inheritsAllPermissions) {
          {{ 'apiKeys.scope.inheritAll' | translate }}
        } @else {
          {{ 'apiKeys.scope.restricted' | translate: { count: apiKey().restrictedPermissionCount } }}
        }
      </span>
      <span
        class="sc-api-key-list-row__stat"
        [class.sc-api-key-list-row__stat--success]="apiKey().enabled"
        [class.sc-api-key-list-row__stat--error]="!apiKey().enabled"
      >
        <span [zagIcon]="apiKey().enabled ? 'check_circle' : 'cancel'"></span>
        {{ apiKey().enabled ? ('apiKeys.status.active' | translate) : ('apiKeys.status.disabled' | translate) }}
      </span>
      <span class="sc-api-key-list-row__stat">
        <span zagIcon="schedule"></span>
        {{ apiKey().createdAt | zagDate }}
      </span>
    </div>

    <div class="sc-api-key-list-row__actions">
      <button
        type="button"
        class="sc-api-key-list-row__action"
        [disabled]="disableActions()"
        [attr.aria-label]="apiKey().enabled ? ('apiKeys.action.disable' | translate) : ('apiKeys.action.enable' | translate)"
        (click)="onToggleClick($event)"
      >
        <span [zagIcon]="apiKey().enabled ? 'toggle_on' : 'toggle_off'"></span>
      </button>
      <button
        type="button"
        class="sc-api-key-list-row__action sc-api-key-list-row__action--delete"
        [disabled]="disableActions()"
        [attr.aria-label]="'common.delete' | translate"
        (click)="onDeleteClick($event)"
      >
        <span zagIcon="delete"></span>
      </button>
    </div>
  `,
  styles: [
    `
      .sc-api-key-list-row {
        display: flex;
        align-items: center;
        padding: var(--spacing-sm) var(--spacing-md);
        gap: var(--spacing-md);
        cursor: pointer;
        transition: box-shadow 0.15s ease;
        background-color: var(--surface-color-default);
        border: 1px solid var(--border-color-default);
        border-radius: var(--border-radius-md);
      }

      .sc-api-key-list-row:focus-visible {
        outline: 2px solid var(--color-primary);
        outline-offset: 2px;
      }

      .sc-api-key-list-row__icon {
        font-size: 24px;
        color: var(--color-primary);
        flex-shrink: 0;
      }

      .sc-api-key-list-row__info {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;
      }

      .sc-api-key-list-row__name {
        font-weight: 500;
        color: var(--text-color-primary);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .sc-api-key-list-row__prefix {
        font-family: monospace;
        font-size: var(--font-size-sm);
        color: var(--text-color-secondary);
      }

      .sc-api-key-list-row__stats {
        display: flex;
        align-items: center;
        gap: var(--spacing-md);
        flex-shrink: 0;
      }

      .sc-api-key-list-row__stat {
        display: flex;
        align-items: center;
        gap: var(--spacing-xs);
        font-size: var(--font-size-sm);
        color: var(--text-color-tertiary);
      }

      .sc-api-key-list-row__stat [zagIcon] {
        font-size: 16px;
      }

      .sc-api-key-list-row__stat--success {
        color: var(--color-success);
      }

      .sc-api-key-list-row__stat--warning {
        color: var(--color-warning);
      }

      .sc-api-key-list-row__stat--error {
        color: var(--color-error);
      }

      .sc-api-key-list-row__actions {
        display: flex;
        align-items: center;
        gap: var(--spacing-xs);
        flex-shrink: 0;
      }

      .sc-api-key-list-row__action {
        flex-shrink: 0;
        padding: var(--spacing-xs);
        color: var(--text-color-secondary);
        border: none;
        background: none;
        border-radius: var(--border-radius-sm);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition:
          background-color 0.15s,
          color 0.15s;
      }

      .sc-api-key-list-row__action:hover {
        background-color: var(--color-info-light);
        color: var(--color-primary);
      }

      .sc-api-key-list-row__action:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .sc-api-key-list-row__action:disabled:hover {
        background-color: transparent;
        color: var(--text-color-secondary);
      }

      .sc-api-key-list-row__action [zagIcon] {
        font-size: 18px;
      }

      .sc-api-key-list-row__action--delete:hover:not(:disabled) {
        background-color: var(--color-error-light);
        color: var(--color-error);
      }

      @media (max-width: 768px) {
        .sc-api-key-list-row__stats {
          display: none;
        }
      }
    `,
  ],
})
export class ApiKeyListRowComponent {
  private readonly router = inject(Router);

  readonly apiKey = input.required<ApiKeyListRowData>();
  readonly disableActions = input(false);

  /** Route for navigation on row click (array format for routerLink). */
  readonly editRoute = input<string[]>();

  /** Emitted when the user clicks the toggle action. Parent handles the API call. */
  readonly toggleEnabled = output<ApiKeyListRowData>();

  /** Emitted when the user clicks delete. Parent shows confirmation dialog. */
  readonly deleteKey = output<ApiKeyListRowData>();

  onRowClick(): void {
    const route = this.editRoute();
    if (route) {
      void this.router.navigate(route);
    }
  }

  onRowKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest('button')) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onRowClick();
    }
  }

  onToggleClick(event: Event): void {
    event.stopPropagation();
    this.toggleEnabled.emit(this.apiKey());
  }

  onDeleteClick(event: Event): void {
    event.stopPropagation();
    this.deleteKey.emit(this.apiKey());
  }
}
